import axios from 'axios';
import { createContext, useContext, useEffect, useState } from 'react';
import { getApiUrl } from '../utils/config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || sessionStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Check if user is authenticated on app load
  const checkAuth = async () => {
    if (token) {
      try {
        const response = await axios.get(getApiUrl('/auth/me'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data.data.user);
      } catch (error) {
        console.error('Auth check failed:', error);
        logout();
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    checkAuth();
  }, [token]);

  const login = async (login, password, rememberMe = false) => {
    try {
      const response = await axios.post(getApiUrl('/auth/login'), {
        login, // can be email or phone
        password,
        remember_me: rememberMe,
      });
      // Extract from response.data.data
      const { token: newToken, user: userData } = response.data.data;
      setToken(newToken);
      setUser(userData);
      // Store token based on remember me preference
      if (rememberMe) {
        localStorage.setItem('token', newToken);
        localStorage.setItem('remember_me', 'true');
      } else {
        sessionStorage.setItem('token', newToken);
        localStorage.removeItem('remember_me');
      }
      // Store user data
      localStorage.setItem('user', JSON.stringify(userData));
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed',
      };
    }
  };

  const register = async (userData, rememberMe = false) => {
    try {
      const response = await axios.post(getApiUrl('/auth/register'), userData);
      // Extract from response.data.data
      const { token: newToken, user: userInfo } = response.data.data;
      setToken(newToken);
      setUser(userInfo);
      // Store token based on remember me preference (like login)
      if (rememberMe) {
        localStorage.setItem('token', newToken);
        localStorage.setItem('remember_me', 'true');
      } else {
        sessionStorage.setItem('token', newToken);
        localStorage.removeItem('remember_me');
      }
      // Store user data
      localStorage.setItem('user', JSON.stringify(userInfo));
      return { success: true, user: userInfo };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed',
      };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(getApiUrl('/auth/logout'), {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setToken(null);
      setUser(null);
      // Clear both storage types
      localStorage.removeItem('token');
      localStorage.removeItem('remember_me');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
    }
  };

  const deleteAccount = async () => {
    try {
      await axios.delete(getApiUrl('/auth/delete-account'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      logout();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete account',
      };
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    deleteAccount,
    updateUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isUser: user?.role === 'user',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 